import { Inject, Injectable } from '@angular/core'
import { ActivatedRouteSnapshot, Resolve, Router, RouterStateSnapshot } from '@angular/router'
import { combineLatest, EMPTY, Observable, of, throwError } from 'rxjs'
import { CollectionsService } from '../libs/collections/services/collections.service'
import { defaultCollectionRequestParams } from '../libs/collections/default-collection-request-params'
import { catchError, map, switchMap, take } from 'rxjs/operators'
import { LOGGER_SERVICE } from '../logger/logger.config'
import { LoggerService } from '../logger/logger.interface'
import { ExperimentationService } from '../shared/experimentation/experimentation.service'
import {
  EXP_NEW_ATHLETE_PROFILES_ID,
  EXP_NEW_ATHLETE_PROFILES_VARIATION,
  EXP_NEW_COLLECTION_ENTITY_HUBS_ID,
  EXP_NEW_COLLECTION_ENTITY_HUBS_VARIATION
} from '../shared/experimentation/experiment.model'
import { FloCoreApiResponseCollection, FloCoreApiResponseCollections } from 'src/io/models/http/flo-core-api-response'
import { DefaultMeta } from 'src/io/models/http/flo-core-api-meta'
import { getIdFromSlug } from '../shared/utility-functions/slug.utility'

type CollectionData = {
  collectionRes?:
    | FloCoreApiResponseCollection
    | {
        data: {
          short_title?: string | null
          id?: number
        }
        meta: DefaultMeta
      }
  params?: {
    collectionId: string | undefined
  }
  relatedCollections?: FloCoreApiResponseCollections
}
@Injectable()
export class CollectionsResolver implements Resolve<CollectionData> {
  constructor(
    private router: Router,
    private readonly collectionsService: CollectionsService,
    private readonly es: ExperimentationService,
    @Inject(LOGGER_SERVICE)
    private readonly logger: LoggerService
  ) {}

  public resolve(route: ActivatedRouteSnapshot, state: RouterStateSnapshot): Observable<CollectionData> {
    const collectionId = !!route.params.collectionId ? getIdFromSlug(route.params.collectionId).toString() : undefined
    const collectionTag = route.params.collectionTag
    const nodeId = !!route.params.nodeId ? getIdFromSlug(route.params.nodeId).toString() : undefined
    const parentRoute = route.parent?.url?.toString()
    return combineLatest([
      this.es.isInVariation(
        EXP_NEW_ATHLETE_PROFILES_ID,
        EXP_NEW_ATHLETE_PROFILES_VARIATION,
        parentRoute?.startsWith('people') || false
      ),
      this.es.isInVariation(
        EXP_NEW_COLLECTION_ENTITY_HUBS_ID,
        EXP_NEW_COLLECTION_ENTITY_HUBS_VARIATION,
        !!parentRoute?.match(/^collections\/(\d+)/) || false
      )
    ]).pipe(
      switchMap(([isInAthletesVariation, isInCollectionsVariation]) => {
        if (
          (isInAthletesVariation && parentRoute?.startsWith('people')) ||
          (isInCollectionsVariation && !!parentRoute?.match(/^collections\/(\d+)/))
        ) {
          // Exit early before calling out for old collection page data
          // We will request new collection page data from BFF in the smart component
          return of({})
        }

        return this.getOldCollectionPageData(route, collectionId, collectionTag, nodeId)
      }),
      /**
       * take(1) is necessary here because all observables must complete within a resolver.
       * isInVariation doesn't complete on server
       * see @ExperimentationService activate method
       */
      take(1)
    )
  }

  /**
   * Gets collection data from core api for older collection pages that use the collection facade.
   * @param route The activated route of the link clicked ex: /collection/${nodeId}
   * @returns CollectionData
   */
  private getOldCollectionPageData = (
    route: ActivatedRouteSnapshot,
    collectionId?: string,
    collectionTag?: string,
    nodeId?: string
  ): Observable<CollectionData> => {
    const includeNode = this.sanitizeIncludeNode(route.queryParams.includeNode)
    const requestParams = {
      ...defaultCollectionRequestParams,
      collectionId,
      collectionTag,
      include_node: includeNode,
      type: route.params.tab,
      view: route.queryParams.view || 'Completed',
      nodeId
    }

    if (collectionId) {
      return combineLatest([
        this.collectionsService.getCollectionById(collectionId, requestParams),
        of(requestParams),
        this.collectionsService.getRelatedCollections(collectionId)
      ]).pipe(
        map(([collectionRes, params, relatedCollections]) => ({
          collectionRes,
          params,
          relatedCollections
        })),
        catchError(err => {
          this.logger.error(`CollectionResolver: CollectionId Error - ${collectionId}`, err)
          return EMPTY
        })
      )
    } else if (collectionTag) {
      return combineLatest([
        this.collectionsService
          .getCollectionByTag(collectionTag, requestParams)
          .pipe(map(response => ({ ...response, data: { tag: requestParams.collectionTag, ...response.data } }))),
        of(requestParams),
        this.collectionsService.getRecentlyPublishedCollections()
      ]).pipe(
        map(([collectionRes, params, relatedCollections]) => ({
          collectionRes,
          params,
          relatedCollections
        })),
        catchError(err => {
          this.logger.error(`CollectionResolver: Tag Error - ${collectionTag}`, err)
          return EMPTY
        })
      )
    } else if (nodeId) {
      return combineLatest([
        this.collectionsService
          .getCollectionByNodeId(Number(nodeId), requestParams)
          .pipe(map(response => ({ ...response, data: { nodeId: requestParams.nodeId, ...response.data } }))),
        of(requestParams),
        this.collectionsService.getRecentlyPublishedCollections()
      ]).pipe(
        map(([collectionRes, params, relatedCollections]) => ({
          collectionRes,
          params,
          relatedCollections
        })),
        catchError(err => {
          this.logger.error(`CollectionResolver: NodeId Error - ${nodeId}`, err)
          return EMPTY
        })
      )
    } else {
      return throwError('CollectionResolver: no collection id or tag provided').pipe(
        catchError(err => {
          this.router.navigate(['**'], { skipLocationChange: true })
          return EMPTY
        })
      )
    }
  }

  public sanitizeIncludeNode = (includeNode: string | undefined): number | undefined => {
    return includeNode ? parseInt(decodeURIComponent(includeNode), 10) : undefined
  }
}
